import "./style.css";
import React, { useEffect, useRef, useState } from "react";
import Tooltip from "../Tooltip";
import {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    List,
    ListOrdered,
} from "lucide-react";

interface MarkdownEditorProps {
    value?: string;
    onChange?: (value: string | React.ChangeEvent<HTMLTextAreaElement>) => void;
    draft?: string;
    setDraft?: React.Dispatch<React.SetStateAction<string>>;
    placeholder?: string;
    className?: string;
    ["data-testid"]?: string;
    variant?: "plain" | "textarea" | "markdown";
    label?: string;
    disabled?: boolean;
    showButtons?: boolean;
    onAccept?: () => void;
    onReject?: () => void;
    buttonState?: boolean;
    showToolbar?: boolean;
    onSend?: () => void;
    showSendButton?: boolean;
    canSend?: boolean;
    isLoading?: boolean;
    height?: number;
}

export default function MarkdownEditor({
    value = "## Write markdown here...",
    onChange,
    draft,
    setDraft,
    placeholder = "Start writing your markdown here...",
    className = "",
    "data-testid": testId,
    variant = "plain",
    label = "",
    disabled = false,
    showButtons = false,
    onAccept,
    onReject,
    buttonState = true,
    showToolbar = false,
    onSend,
    showSendButton = false,
    canSend = true,
    isLoading = false,
    height,
}: MarkdownEditorProps) {
    const [internalDraft, setInternalDraft] = useState(value);
    const [isButtonActive, setIsButtonActive] = useState(buttonState);
    const textareaRef = useRef<HTMLTextAreaElement>(null);



    useEffect(() => {
        if (variant === "markdown" && textareaRef.current && textareaRef.current.value !== value) {
            textareaRef.current.value = value;
        }
    }, [value, variant]);

    const handleChange = (...args: (string | React.ChangeEvent<HTMLTextAreaElement>)[]) => {
        let stringValue: string;
        let eventObject: React.ChangeEvent<HTMLTextAreaElement> | null = null;

        if (args.length > 0 && typeof args[0] === 'string') {
            stringValue = args[0];
        } else if (args.length > 0 && typeof args[0] === 'object') {
            eventObject = args[0];
            stringValue = args[0].target.value;
        } else {
            stringValue = '';
        }

        if (setDraft) {
            setDraft(stringValue || '');
        } else {
            setInternalDraft(stringValue || '');
        }
        if (onChange) {
            if (variant === "textarea" && eventObject) {
                onChange(eventObject);
            } else {
                onChange(stringValue);
            }
        }
    };

    const handleMarkdownChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (onChange) onChange(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showSendButton) return;
        if (!onSend) return;
        if (!canSend || isLoading) return;
        if (disabled) return;

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    const insertText = (before: string, after: string = "") => {
        const textarea = textareaRef.current;
        if (textarea) {
            const currentValue = textarea.value;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = currentValue.substring(start, end);
            const newText = currentValue.substring(0, start) + before + selectedText + after + currentValue.substring(end);
            if (variant === "markdown") {
                textarea.value = newText;
                if (onChange) onChange(newText);
            } else {
                handleChange(newText);
            }
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
            }, 0);
        } else {
            const currentValue = draft !== undefined ? draft : internalDraft || value;
            handleChange(currentValue + before + after);
        }
    };

    const insertListMarkdown = (prefix: string) => {
        const textarea = textareaRef.current;
        if (textarea) {
            const currentValue = textarea.value;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = currentValue.substring(start, end);

            let newText: string;
            let newStart: number;
            let newEnd: number;

            if (start !== end && selectedText.includes("\n")) {
                const lines = selectedText.split("\n");
                const prefixed = lines
                    .map((line, i) => {
                        if (!line.trim()) return line;
                        const marker = prefix === "1. " ? `${i + 1}. ` : prefix;
                        return marker + line;
                    })
                    .join("\n");
                newText = currentValue.substring(0, start) + prefixed + currentValue.substring(end);
                newStart = start;
                newEnd = start + prefixed.length;
            } else {
                newText = currentValue.substring(0, start) + prefix + currentValue.substring(start);
                newStart = start + prefix.length;
                newEnd = newStart;
            }

            if (variant === "markdown") {
                textarea.value = newText;
                if (onChange) onChange(newText);
            } else {
                handleChange(newText);
            }
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(newStart, newEnd);
            }, 0);
        } else {
            const currentValue = draft !== undefined ? draft : internalDraft || value;
            handleChange(currentValue + prefix);
        }
    };

    const rootClassName =
        variant === "plain" ? `w-full ${className}` : `w-full max-w-4xl mx-auto ${className}`;

    return (
        <div className={rootClassName}>
            {variant === "plain"
                ? (() => {
                    const displayValue = draft !== undefined ? draft : internalDraft || value || "Add your input to generate results";
                    const isEmpty = !displayValue || displayValue === "Add your input to generate results";
                    return (
                        <>
                            {label && <label className="text-sm font-semibold text-deep-blue">{label}</label>}
                            <div className={`mt-1 w-full leading-relaxed ${isEmpty ? "text-gray-400" : "text-black"}`} data-testid={testId}>
                                {displayValue}
                            </div>
                        </>
                    );
                })()
                : variant === "textarea" ? (
                    <>
                        {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
                        <textarea
                            value={draft !== undefined ? draft : internalDraft || value}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            className="w-full min-h-11 resize-none rounded-xl border border-gray-400 bg-white px-3 py-2 text-black"
                            data-testid={testId}
                        />
                    </>
                ) : (
                    <>
                        {label && (
                            <div className="mb-2">
                                <label className="text-md md:text-lg font-bold text-gray-700">{label}</label>
                            </div>
                        )}
                        <div className={`rounded-lg overflow-hidden border border-gray-200 shadow-sm`}>
                            {showToolbar && (
                                <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-100 flex-wrap">
                                    <Tooltip content="Bold" showArrow={true}>
                                        <button
                                            type="button"
                                            onClick={() => insertText("**", "**")}
                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-700 font-bold"
                                            disabled={disabled}
                                        >
                                            <Bold size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Italic" showArrow={true}>
                                        <button
                                            type="button"
                                            onClick={() => insertText("*", "*")}
                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-700 italic"
                                            disabled={disabled}
                                        >
                                            <Italic size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Underline" showArrow={true}>
                                        <button
                                            type="button"
                                            onClick={() => insertText("<u>", "</u>")}
                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                            disabled={disabled}
                                        >
                                            <Underline size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Strikethrough" showArrow={true}>
                                        <button
                                            type="button"
                                            onClick={() => insertText("~~", "~~")}
                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                            disabled={disabled}
                                        >
                                            <Strikethrough size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Unordered List" showArrow={true}>
                                        <button
                                            type="button"
                                            onClick={() => insertListMarkdown("- ")}
                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                            disabled={disabled}
                                        >
                                            <List size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Ordered List" showArrow={true}>
                                        <button
                                            type="button"
                                            onClick={() => insertListMarkdown("1. ")}
                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                            disabled={disabled}
                                        >
                                            <ListOrdered size={16} />
                                        </button>
                                    </Tooltip>
                                </div>
                            )}
                            <div className="relative">

                                <textarea
                                    ref={textareaRef}
                                    defaultValue={value}
                                    onChange={handleMarkdownChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                    data-testid={testId}
                                    style={{ height: height ?? 160 }}
                                    className="w-full resize-none bg-white px-3 py-2 text-sm text-black outline-none font-sans"
                                />
                                {showButtons && (
                                    <div className="flex gap-2 absolute bottom-2 right-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsButtonActive(!isButtonActive);
                                                if (onAccept) onAccept();
                                            }}
                                            className={`p-1.5 rounded-full ${isButtonActive ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="align-middle">
                                                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                                                <path d="m9 12 2 2 4-4"></path>
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsButtonActive(!isButtonActive);
                                                if (onReject) onReject();
                                            }}
                                            className={`p-1.5 rounded-full ${!isButtonActive ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="align-middle">
                                                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                                                <path d="m15 9-6 6"></path>
                                                <path d="m9 9 6 6"></path>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                {showSendButton && (
                                    <div className="absolute bottom-2 right-2">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (onSend && canSend && !isLoading) {
                                                    onSend();
                                                }
                                            }}
                                            disabled={!canSend || isLoading}
                                            title={isLoading ? 'Sending...' : 'Send'}
                                            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-500 text-white shadow hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 19V5" />
                                                <path d="m5 12 7-7 7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
        </div>
    );
}
